import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';

/** Options passed when constructing the HUD. */
export interface HUDOptions {
  maxHp?: number;
}

// ── Layout constants ─────────────────────────────────────────────────────────
const MARGIN = 12;
const HP_BAR_W = 180;
const HP_BAR_H = 22;
const MINIMAP_SIZE = 120;

const FONT_BOLD: Partial<TextStyle> = {
  fontFamily: 'Arial',
  fontWeight: 'bold',
  fill: 0xffffff,
  stroke: { color: 0x000000, width: 3 },
};

function makeText(text: string, size: number): Text {
  return new Text({ text, style: new TextStyle({ ...FONT_BOLD, fontSize: size }) });
}

/**
 * HUD is a screen-space Container (added directly to app.stage, unaffected
 * by the world camera).
 *
 * Regions:
 *  - Bottom-left : HP bar + HP numbers
 *  - Top-left    : Kill counter
 *  - Top-centre  : Alive count
 *  - Centre      : Zone warning (hidden by default)
 *  - Bottom-right: 120×120 minimap placeholder
 */
export class HUD extends Container {
  private app: Application;
  private maxHp: number;

  // ── HP bar ─────────────────────────────────────────────────────────────────
  private hpBarBg!:  Graphics;
  private hpBarFg!:  Graphics;
  private hpLabel!:  Text;

  // ── Kill counter ───────────────────────────────────────────────────────────
  private killsLabel!: Text;

  // ── Alive count ────────────────────────────────────────────────────────────
  private aliveLabel!: Text;

  // ── Zone warning ───────────────────────────────────────────────────────────
  private zoneWarning!: Container;

  // ── Minimap ────────────────────────────────────────────────────────────────
  private minimapContainer!: Container;
  private minimapBg!:        Graphics;
  private minimapDots!:      Graphics;  // redrawn each call to updateMinimap

  constructor(app: Application, options: HUDOptions = {}) {
    super();
    this.app    = app;
    this.maxHp  = options.maxHp ?? 100;

    this._buildHpBar();
    this._buildKills();
    this._buildAlive();
    this._buildZoneWarning();
    this._buildMinimap();
  }

  // ── Build helpers ─────────────────────────────────────────────────────────

  private _buildHpBar(): void {
    const screenH = this.app.screen.height;
    const y = screenH - MARGIN - HP_BAR_H;

    // background pill
    this.hpBarBg = new Graphics();
    this.hpBarBg.roundRect(MARGIN, y, HP_BAR_W, HP_BAR_H, 6);
    this.hpBarBg.fill({ color: 0x330000 });
    this.hpBarBg.stroke({ color: 0x000000, width: 2 });
    this.addChild(this.hpBarBg);

    // foreground fill (redrawn on setHp)
    this.hpBarFg = new Graphics();
    this.addChild(this.hpBarFg);

    // "HP: 100" label
    this.hpLabel = makeText('HP: 100', 12);
    this.hpLabel.anchor.set(0, 0.5);
    this.hpLabel.position.set(MARGIN + HP_BAR_W + 8, y + HP_BAR_H / 2);
    this.addChild(this.hpLabel);

    // Draw initial full bar
    this._redrawHpBar(100);
  }

  private _redrawHpBar(hp: number): void {
    const screenH = this.app.screen.height;
    const y       = screenH - MARGIN - HP_BAR_H;
    const ratio   = this.maxHp > 0 ? Math.max(0, Math.min(hp, this.maxHp)) / this.maxHp : 0;
    const fillW   = HP_BAR_W * ratio;

    let color: number;
    if      (ratio > 0.5)  color = 0x44dd44;
    else if (ratio > 0.25) color = 0xddcc22;
    else                   color = 0xdd3333;

    this.hpBarFg.clear();
    if (fillW > 0) {
      this.hpBarFg.roundRect(MARGIN, y, fillW, HP_BAR_H, 6);
      this.hpBarFg.fill({ color });
    }

    this.hpLabel.text = `HP: ${Math.round(hp)}`;
  }

  private _buildKills(): void {
    this.killsLabel = makeText('Kills: 0', 16);
    this.killsLabel.position.set(MARGIN, MARGIN);
    this.addChild(this.killsLabel);
  }

  private _buildAlive(): void {
    const screenW = this.app.screen.width;
    this.aliveLabel = makeText('👥 100 alive', 18);
    this.aliveLabel.anchor.set(0.5, 0);
    this.aliveLabel.position.set(screenW / 2, MARGIN);
    this.addChild(this.aliveLabel);
  }

  private _buildZoneWarning(): void {
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;

    this.zoneWarning = new Container();
    this.zoneWarning.visible = false; // hidden by default

    // Semi-transparent pill background
    const bg = new Graphics();
    const pillW = 280;
    const pillH = 40;
    bg.roundRect(-pillW / 2, -pillH / 2, pillW, pillH, 10);
    bg.fill({ color: 0xcc0000, alpha: 0.75 });
    this.zoneWarning.addChild(bg);

    const warn = makeText('⚠  GET IN THE ZONE!', 16);
    warn.anchor.set(0.5);
    this.zoneWarning.addChild(warn);

    this.zoneWarning.position.set(screenW / 2, screenH / 2 - 60);
    this.addChild(this.zoneWarning);
  }

  private _buildMinimap(): void {
    const screenW = this.app.screen.width;
    const screenH = this.app.screen.height;
    const x = screenW  - MINIMAP_SIZE - MARGIN;
    const y = screenH  - MINIMAP_SIZE - MARGIN;

    this.minimapContainer = new Container();
    this.minimapContainer.position.set(x, y);

    // Background
    this.minimapBg = new Graphics();
    this.minimapBg.roundRect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE, 6);
    this.minimapBg.fill({ color: 0x1a3a1a, alpha: 0.85 });
    this.minimapBg.stroke({ color: 0x888888, width: 2 });
    this.minimapContainer.addChild(this.minimapBg);

    // Dots layer (drawn on each updateMinimap call)
    this.minimapDots = new Graphics();
    this.minimapContainer.addChild(this.minimapDots);

    this.addChild(this.minimapContainer);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Update the HP bar. */
  setHp(hp: number, maxHp?: number): void {
    if (maxHp !== undefined) this.maxHp = maxHp;
    this._redrawHpBar(hp);
  }

  /** Update the kill counter display. */
  setKills(kills: number): void {
    this.killsLabel.text = `Kills: ${kills}`;
  }

  /** Update the alive-count display. */
  setAliveCount(count: number): void {
    this.aliveLabel.text = `👥 ${count} alive`;
  }

  /** Show or hide the zone warning banner. */
  setZoneWarning(visible: boolean): void {
    this.zoneWarning.visible = visible;
  }

  /**
   * Redraw minimap dots.
   * @param players  array of { x, y } in world-space (0..mapWidth/mapHeight)
   * @param mapW     world map pixel width
   * @param mapH     world map pixel height
   * @param localPos local player world position (highlighted differently)
   */
  updateMinimap(
    players: { x: number; y: number }[],
    mapW: number,
    mapH: number,
    localPos?: { x: number; y: number },
  ): void {
    this.minimapDots.clear();

    const scaleX = MINIMAP_SIZE / mapW;
    const scaleY = MINIMAP_SIZE / mapH;

    // Remote players — small white dots
    for (const p of players) {
      const mx = p.x * scaleX;
      const my = p.y * scaleY;
      this.minimapDots.circle(mx, my, 2);
      this.minimapDots.fill({ color: 0xffffff, alpha: 0.8 });
    }

    // Local player — larger yellow dot
    if (localPos) {
      const mx = localPos.x * scaleX;
      const my = localPos.y * scaleY;
      this.minimapDots.circle(mx, my, 4);
      this.minimapDots.fill({ color: 0xffee00 });
      this.minimapDots.circle(mx, my, 4);
      this.minimapDots.stroke({ color: 0x000000, width: 1 });
    }
  }
}
